import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Descriptions,
  Typography,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Alert,
  Spin,
  Progress,
} from 'antd';
import {
  ReloadOutlined,
  ClockCircleOutlined,
  CloudServerOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';

const { Title } = Typography;

interface InstanceInfo {
  id: string;
  az: string;
  privateIp: string;
  instanceType?: string;
  region?: string;
  isEc2?: boolean;
}

interface Diagnostics {
  instance?: InstanceInfo;
  startedAt: string;
  uptimeSeconds: number;
  uptime: string;
  build: {
    commit: string;
    builtAt: string;
    nodeVersion: string;
    platform: string;
    arch: string;
  };
  runtime: {
    pid: number;
    hostname: string;
    memoryMB: {
      rss: number;
      heapUsed: number;
      heapTotal: number;
      external: number;
    };
    systemMemoryMB: {
      total: number;
      free: number;
    };
    loadAvg: number[];
    cpus: number;
    osType: string;
    osRelease: string;
  };
  env: {
    NODE_ENV: string;
    APP_PORT: string;
    API_PREFIX: string;
  };
}

// How many parallel calls to fan out when polling the fleet.
// 30 calls usually reaches every node behind an 8-node ALB target group.
const FLEET_POLL_FANOUT = 30;

export const DiagnosticsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Diagnostics | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fleet view state
  const [fleet, setFleet] = useState<Map<string, Diagnostics>>(new Map());
  const [fleetLoading, setFleetLoading] = useState(false);
  const [fleetProgress, setFleetProgress] = useState(0);

  const fetchDiagnostics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getDiagnostics();
      setData(res.data);
    } catch (err: unknown) {
      const e = err as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (e.response?.status === 403) {
        setError('Forbidden — your admin role lacks config:show permission.');
      } else if (e.response?.status === 401) {
        setError('Unauthorized — admin session expired.');
      } else {
        setError(e.response?.data?.message || 'Failed to load diagnostics');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const pollFleet = useCallback(async () => {
    setFleetLoading(true);
    setFleetProgress(0);
    const found = new Map<string, Diagnostics>();
    let completed = 0;

    const calls = Array.from({ length: FLEET_POLL_FANOUT }, () =>
      adminApi
        .getDiagnostics()
        .then((res) => {
          const d = res.data as Diagnostics;
          const id = d.instance?.id;
          if (id && !found.has(id)) {
            found.set(id, d);
          }
        })
        .catch(() => {
          // swallow per-call errors; surfaced via empty fleet map
        })
        .finally(() => {
          completed += 1;
          setFleetProgress(Math.round((completed / FLEET_POLL_FANOUT) * 100));
        })
    );

    await Promise.all(calls);
    setFleet(found);
    setFleetLoading(false);
  }, []);

  useEffect(() => {
    fetchDiagnostics();
  }, [fetchDiagnostics]);

  if (loading && !data) {
    return (
      <Card>
        <Spin tip="Loading diagnostics..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          type="error"
          message="Couldn't load diagnostics"
          description={error}
          action={
            <Button icon={<ReloadOutlined />} onClick={() => fetchDiagnostics()}>
              Retry
            </Button>
          }
        />
      </Card>
    );
  }

  if (!data) return null;

  const heapPct = Math.round(
    (data.runtime.memoryMB.heapUsed / data.runtime.memoryMB.heapTotal) * 100
  );
  const sysMemPct = Math.round(
    ((data.runtime.systemMemoryMB.total - data.runtime.systemMemoryMB.free) /
      data.runtime.systemMemoryMB.total) *
      100
  );

  const instance = data.instance;
  const fleetCards = Array.from(fleet.values()).sort((a, b) => {
    const aId = a.instance?.id || '';
    const bId = b.instance?.id || '';
    return aId.localeCompare(bId);
  });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Backend Diagnostics
          </Title>
          <Typography.Text type="secondary">
            Runtime + build metadata for the live <code>wcc4-api</code> container. Admin-only.
          </Typography.Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<ThunderboltOutlined />}
              onClick={() => pollFleet()}
              loading={fleetLoading}
              type="default"
            >
              Poll fleet ({FLEET_POLL_FANOUT}×)
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => fetchDiagnostics()} loading={loading}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Instance identity — prominent, color-coded */}
      {instance && (
        <Card
          size="small"
          style={{
            background: instance.isEc2 ? '#e6f4ff' : '#fff7e6',
            borderColor: instance.isEc2 ? '#1677ff' : '#fa8c16',
          }}
          bodyStyle={{ padding: '12px 16px' }}
        >
          <Space size="large" wrap>
            <Space>
              <CloudServerOutlined
                style={{
                  fontSize: 20,
                  color: instance.isEc2 ? '#1677ff' : '#fa8c16',
                }}
              />
              <Typography.Text strong>
                {instance.isEc2 ? 'EC2 instance' : 'Local / non-EC2'}
              </Typography.Text>
            </Space>
            <Typography.Text>
              <Typography.Text type="secondary">ID:</Typography.Text> <code>{instance.id}</code>
            </Typography.Text>
            <Typography.Text>
              <Typography.Text type="secondary">AZ:</Typography.Text>{' '}
              <Tag color={instance.isEc2 ? 'blue' : 'orange'}>{instance.az}</Tag>
            </Typography.Text>
            <Typography.Text>
              <Typography.Text type="secondary">Private IP:</Typography.Text>{' '}
              <code>{instance.privateIp}</code>
            </Typography.Text>
            {instance.instanceType && (
              <Typography.Text>
                <Typography.Text type="secondary">Type:</Typography.Text>{' '}
                <Tag>{instance.instanceType}</Tag>
              </Typography.Text>
            )}
            {instance.region && (
              <Typography.Text>
                <Typography.Text type="secondary">Region:</Typography.Text>{' '}
                <code>{instance.region}</code>
              </Typography.Text>
            )}
          </Space>
        </Card>
      )}

      {/* Fleet view — appears after first poll */}
      {(fleetLoading || fleet.size > 0) && (
        <Card
          title={
            <Space>
              <ThunderboltOutlined />
              <span>Fleet view</span>
              {!fleetLoading && (
                <Tag color="blue">
                  {fleet.size} unique instance{fleet.size === 1 ? '' : 's'} discovered in{' '}
                  {FLEET_POLL_FANOUT} calls
                </Tag>
              )}
            </Space>
          }
          extra={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => pollFleet()}
              loading={fleetLoading}
            >
              Re-poll
            </Button>
          }
        >
          {fleetLoading && <Progress percent={fleetProgress} status="active" size="small" />}
          {!fleetLoading && fleet.size === 0 && (
            <Alert
              type="warning"
              message="No instances discovered"
              description="All polling calls failed. Either the endpoint is unreachable or every response was missing the `instance.id` field."
            />
          )}
          {!fleetLoading && fleet.size > 0 && (
            <Row gutter={[16, 16]}>
              {fleetCards.map((d) => {
                const ii = d.instance!;
                return (
                  <Col xs={24} md={12} lg={8} key={ii.id}>
                    <Card size="small" hoverable>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Typography.Text strong>
                          <code>{ii.id}</code>
                        </Typography.Text>
                        <Space size="small" wrap>
                          <Tag color="blue">{ii.az}</Tag>
                          {ii.instanceType && <Tag>{ii.instanceType}</Tag>}
                        </Space>
                        <Typography.Text type="secondary">{ii.privateIp}</Typography.Text>
                        <Typography.Text type="secondary">uptime: {d.uptime}</Typography.Text>
                        <Typography.Text type="secondary">
                          commit:{' '}
                          <code style={{ fontSize: 11 }}>
                            {d.build.commit === 'unknown'
                              ? 'unknown'
                              : d.build.commit.substring(0, 8)}
                          </code>
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          heap: {d.runtime.memoryMB.heapUsed} / {d.runtime.memoryMB.heapTotal} MB
                        </Typography.Text>
                        <Typography.Text type="secondary">
                          load: {d.runtime.loadAvg.map((v) => v.toFixed(2)).join(' / ')}
                        </Typography.Text>
                      </Space>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card>
      )}

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="Uptime" value={data.uptime} prefix={<ClockCircleOutlined />} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Started {new Date(data.startedAt).toLocaleString()}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Heap Used"
              value={data.runtime.memoryMB.heapUsed}
              suffix={`/ ${data.runtime.memoryMB.heapTotal} MB (${heapPct}%)`}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              RSS {data.runtime.memoryMB.rss} MB
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title="System Memory" value={sysMemPct} suffix="% used" />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {data.runtime.systemMemoryMB.free} MB free of {data.runtime.systemMemoryMB.total} MB
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Card title="Build">
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Git commit">
            {data.build.commit === 'unknown' ? (
              <Tag color="default">unknown</Tag>
            ) : (
              <code>{data.build.commit}</code>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Built at">{data.build.builtAt}</Descriptions.Item>
          <Descriptions.Item label="Node version">{data.build.nodeVersion}</Descriptions.Item>
          <Descriptions.Item label="Platform / arch">
            {data.build.platform} / {data.build.arch}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Runtime">
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="PID">{data.runtime.pid}</Descriptions.Item>
          <Descriptions.Item label="Container hostname">
            <code>{data.runtime.hostname}</code>
          </Descriptions.Item>
          <Descriptions.Item label="CPU count">{data.runtime.cpus}</Descriptions.Item>
          <Descriptions.Item label="Load avg (1m / 5m / 15m)">
            {data.runtime.loadAvg.map((v) => v.toFixed(2)).join(' / ')}
          </Descriptions.Item>
          <Descriptions.Item label="OS">
            {data.runtime.osType} {data.runtime.osRelease}
          </Descriptions.Item>
          <Descriptions.Item label="Started">
            {new Date(data.startedAt).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Environment">
        <Descriptions bordered column={3} size="small">
          <Descriptions.Item label="NODE_ENV">
            <Tag color={data.env.NODE_ENV === 'production' ? 'red' : 'blue'}>
              {data.env.NODE_ENV}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="APP_PORT">{data.env.APP_PORT}</Descriptions.Item>
          <Descriptions.Item label="API_PREFIX">{data.env.API_PREFIX}</Descriptions.Item>
        </Descriptions>
      </Card>
    </Space>
  );
};

export default DiagnosticsPage;
